import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

import {
    searchableDropdownChip,
    searchableDropdownEmptyMessage,
    searchableDropdownList,
    searchableDropdownOption,
    searchableDropdownOverlay,
    searchableDropdownRoot,
} from './components/select'

const menuTheme = {
    root: {
        borderRadius: '{border.radius.md}',
        borderColor: 'var(--tl-color-border)',
        listGap: '0px',
        padding: '0',
        itemPadding: 'var(--tl-padding-1-5) var(--tl-padding-2)',
        itemFocusBackground: 'var(--tl-color-hover-secondary)',
    },
    list: {
        padding: 'var(--tl-padding-1)',
        gap: '0',
    },
}

// note: keep color values in sync with tokens.sass values
export const tlPrimePreset = definePreset(Aura, {
    primitive: {
        borderRadius: {
            none: 'var(--tl-radius-none)',
            xs: 'var(--tl-radius-xs)',
            sm: 'var(--tl-radius-sm)',
            md: 'var(--tl-radius-md)',
            lg: 'var(--tl-radius-lg)',
            xl: 'var(--tl-radius-xl)',
            '2xl': 'var(--tl-radius-2xl)',
            '3xl': 'var(--tl-radius-3xl)',
        },
        white: '#ffffff',
        black: '#000000',
        tlNeutral: {
            50:  '#f9f9fb',
            100: '#f4f5f6',
            150: '#eeeff1',
            200: '#e2e5e9',
            300: '#bcbfc2',
            400: '#a1a5aa',
            500: '#737373',
            600: '#636363',
            700: '#525252',
            800: '#404040',
            900: '#262626',
            950: '#171717',
        },
        gray: {
            50:  '{tlNeutral.50}',
            100: '{tlNeutral.100}',
            150: '{tlNeutral.150}',
            200: '{tlNeutral.200}',
            300: '{tlNeutral.300}',
            400: '{tlNeutral.400}',
            500: '{tlNeutral.500}',
            600: '{tlNeutral.600}',
            700: '{tlNeutral.700}',
            800: '{tlNeutral.800}',
            900: '{tlNeutral.900}',
            950: '{tlNeutral.950}',
        },
        tlBlue: {
            50:  '#f0f5ff',
            100: '#e0e8f3',
            200: '#ccdcfe',
            300: '#99b9fd',
            400: '#6697fb',
            500: '#3374fa',
            600: '#0051f9',
            700: '#0041c7',
            800: '#003094',
            900: '#172554',
            950: '#000f2e',
        },
        tlSapphire: {
            50:  '#fff7f0',
            100: '#ffefe0',
            200: '#ffdec0',
            300: '#ffcea1',
            400: '#ffbd81',
            500: '#ffad62',
            600: '#ff922e',
            700: '#fa7900',
            800: '#c75f00',
            900: '#944700',
            950: '#612e00',
        },
        tlLapis: {
            50:  '#f6f3fb',
            100: '#f0ebf9',
            200: '#e0d8f3',
            300: '#d1c4ed',
            400: '#c2b1e7',
            500: '#b7a2e3',
            600: '#a086da',
            700: '#815fce',
            800: '#633bbf',
            900: '#4f2f98',
            950: '#3b2371',
        },
        tlLightNavy: {
            50:  '#f2f8f8',
            100: '#deedee',
            200: '#bcdcde',
            300: '#9bcacd',
            400: '#79b9bd',
            500: '#58a7ac',
            600: '#45878c',
            700: '#34666a',
            800: '#2c5659',
            900: '#244548',
            950: '#112122',
        },
        sky: {
            50:  '#f2f8f8',
            100: '#deedee',
            200: '#bcdcde',
            300: '#9bcacd',
            400: '#79b9bd',
            500: '#58a7ac',
            600: '#45878c',
            700: '#34666a',
            800: '#2c5659',
            900: '#244548',
            950: '#112122',
        },
        red: {
            50:  '#fef2f2',
            100: '#fee2e2',
            200: '#fecaca',
            300: '#fca5a5',
            400: '#f87171',
            500: '#ef4444',
            600: '#d72323',
            700: '#b91c1c',
            800: '#991b1b',
            900: '#7f1d1d',
            950: '#450a0a',
        },
        orange: {
            50:  '#fff7ed',
            100: '#ffedd5',
            200: '#fed7aa',
            300: '#fdba74',
            400: '#fb923c',
            500: '#f97316',
            600: '#ea580c',
            700: '#c2410c',
            800: '#9a3412',
            900: '#7c2d12',
            950: '#431407',
        },
        amber: {
            50:  '#fffbeb',
            100: '#fef3c7',
            200: '#fde68a',
            300: '#fcd34d',
            400: '#fbbf24',
            500: '#f59e0b',
            600: '#d97706',
            700: '#b45309',
            800: '#92400e',
            900: '#78350f',
            950: '#451a03',
        },
        green: {
            50:  '#f0fdf4',
            100: '#dcfce7',
            200: '#bbf7d0',
            300: '#86efac',
            400: '#4ade80',
            500: '#22c55e',
            600: '#16a34a',
            700: '#15803d',
            800: '#166534',
            900: '#14532d',
            950: '#052e16',
        },
    },

    semantic: {
        disabledOpacity: 'var(--tl-disabled-opacity)',

        // primary maps tlBlue shifted +1 stop so primary.500 = #0051f9 (brand color)
        primary: {
            50: '{tlBlue.100}',
            100: '{tlBlue.200}',
            200: '{tlBlue.300}',
            300: '{tlBlue.400}',
            400: '{tlBlue.500}',
            500: '{tlBlue.600}',
            600: '{tlBlue.700}',
            700: '{tlBlue.800}',
            800: '{tlBlue.900}',
            900: '{tlBlue.950}',
            950: '{tlBlue.950}',
        },

        overlay: {
            modal: {
                borderColor: 'var(--tl-color-border)',
                borderRadius: '{border.radius.lg}',
                padding: 'var(--tl-padding-6)',
                shadow: '0 4px 6px -4px rgba(0, 0, 0, 0.1), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                header: {
                    padding: 'var(--tl-padding-6) var(--tl-padding-6) calc(var(--tl-padding-6) - var(--tl-padding-1-5)) var(--tl-padding-6)',
                },
                content: {
                    padding: 'var(--tl-padding-1-5) var(--tl-padding-6)',
                },
                footer: {
                    padding: 'calc(var(--tl-padding-6) - var(--tl-padding-1-5)) var(--tl-padding-6) var(--tl-padding-6) var(--tl-padding-6)',
                },
            },
        },
        formField: {
            paddingX: 'var(--tl-padding-3)',
            paddingY: 'var(--tl-padding-2-5)',
            sm: {
                paddingX: 'var(--tl-padding-3)',
                paddingY: 'var(--tl-padding-2)',
            },
            borderRadius: '{border.radius.md}',
            focusRing: {
                width: '0',
                style: 'none',
                color: 'transparent',
                offset: '0',
                shadow: 'var(--tl-focus-ring)',
            },
            transitionDuration: '{transition.duration}',
        },
        colorScheme: {
            light: {
                formField: {
                    background: 'var(--tl-color-background)',
                    disabledBackground: 'var(--tl-color-background)',
                    borderColor: 'var(--tl-color-input)',
                    hoverBorderColor: 'var(--tl-color-input)',
                    focusBorderColor: 'var(--tl-color-input)',
                    invalidBorderColor: 'var(--tl-color-input)',
                    color: 'var(--tl-color-foreground)',
                    placeholderColor: 'var(--tl-color-muted-foreground)',
                    invalidPlaceholderColor: 'var(--tl-color-muted-foreground)',
                    floatLabelColor: 'var(--tl-color-foreground)',
                    floatLabelFocusColor: 'var(--tl-color-foreground)',
                    floatLabelActiveColor: 'var(--tl-color-foreground)',
                    shadow: 'none',
                },
                highlight: {
                    background: '{surface.100}',
                },
                overlay: {
                    popover: {
                        background: 'var(--tl-color-popover)',
                        color: 'var(--tl-color-foreground)',
                    },
                },
            },
        },
    },

    components: {
        toast: {
            root: {
                width: '356px',
                borderRadius: '{border.radius.md}',
            },
            content: {
                padding: 'var(--tl-padding-4)',
            },
            text: {
                gap: 'var(--tl-gap-0-5)',
            },
            colorScheme: {
                light: {
                    secondary: {
                        background: 'var(--tl-color-background)',
                        borderColor: 'var(--tl-color-border)',
                        shadow: '{overlay.modal.shadow}',
                    },
                    success: {
                        background: 'var(--tl-color-background)',
                        borderColor: 'var(--tl-color-success)',
                        color: 'var(--tl-color-success)',
                        shadow: '{overlay.modal.shadow}',
                    },
                    error: {
                        background: 'var(--tl-color-background)',
                        borderColor: 'var(--tl-color-destructive)',
                        color: 'var(--tl-color-destructive)',
                        shadow: '{overlay.modal.shadow}',
                    },
                    info: {
                        background: 'var(--tl-color-background)',
                        borderColor: 'var(--tl-color-primary)',
                        shadow: '{overlay.modal.shadow}',
                    },
                    warn: {
                        background: 'var(--tl-color-background)',
                        borderColor: '{yellow.400}',
                        shadow: '{overlay.modal.shadow}',
                    },
                },
            },
        },
        progressspinner: {
            colorScheme: {
                light: {
                    root: {
                        colorOne: 'var(--tl-color-muted)',
                        colorTwo: 'var(--tl-color-muted)',
                        colorThree: 'var(--tl-color-muted)',
                        colorFour: 'var(--tl-color-muted)',
                    },
                },
            },
        },
        skeleton: {
            root: {
                borderRadius: '{border.radius.md}',
            },
            colorScheme: {
                light: {
                    root: {
                        background: 'var(--tl-color-muted)',
                        animationBackground: 'var(--tl-color-background)',
                    },
                },
            },
        },
        tag: {
            root: {
                fontSize: '0.75rem',
                fontWeight: '600',
                padding: 'var(--tl-padding-0-5) var(--tl-padding-2-5)',
                gap: 'var(--tl-gap-1)',
                borderRadius: 'var(--tl-radius-md)',
                roundedBorderRadius: '9999px',
            },
            icon: {
                size: '0.75rem',
            },
            colorScheme: {
                light: {
                    primary: {
                        background: 'var(--tl-color-primary)',
                        color: 'var(--tl-color-primary-foreground)',
                    },
                    secondary: {
                        background: 'var(--tl-color-secondary)',
                        color: 'var(--tl-color-secondary-foreground)',
                    },
                    danger: {
                        background: 'var(--tl-color-destructive)',
                        color: 'var(--tl-color-destructive-foreground)',
                    },
                },
            },
        },
        toggleswitch: {
            colorScheme: {
                light: {
                    root: {
                        background: 'var(--tl-color-input)',
                        hoverBackground: 'var(--tl-color-input)',
                        checkedBackground: 'var(--tl-color-primary)',
                        checkedHoverBackground: 'var(--tl-color-primary)',
                    },
                    handle: {
                        background: 'var(--tl-color-background)',
                        hoverBackground: 'var(--tl-color-background)',
                        checkedBackground: 'var(--tl-color-background)',
                        checkedHoverBackground: 'var(--tl-color-background)',
                    },
                },
            },
        },
        select: {
            root: searchableDropdownRoot,
            overlay: searchableDropdownOverlay,
            list: searchableDropdownList,
            option: {
                ...searchableDropdownOption,
            },
            emptyMessage: searchableDropdownEmptyMessage,
        },
        listbox: {
            list: searchableDropdownList,
            option: {
                ...searchableDropdownOption,
            },
            emptyMessage: searchableDropdownEmptyMessage,
        },
        multiselect: {
            root: searchableDropdownRoot,
            overlay: searchableDropdownOverlay,
            list: searchableDropdownList,
            option: {
                ...searchableDropdownOption,
                gap: 'var(--tl-gap-2)',
            },
            chip: searchableDropdownChip,
            emptyMessage: searchableDropdownEmptyMessage,
        },
        menu: {
            ...menuTheme,
        },
        tieredmenu: {
            ...menuTheme,
            item: {
                activeBackground: 'var(--tl-color-hover-secondary)',
                focusBackground: 'var(--tl-color-hover-secondary)',
                borderRadius: 'var(--tl-radius)',
                icon: {
                    color: 'currentColor',
                    activeColor: 'currentColor',
                    focusColor: 'currentColor',
                },
            },
        },
        paginator: {
            nav: {
                button: {
                    selectedBackground: '{surface.100}',
                    borderRadius: '{border.radius.md}',
                },
            },
        },
        dialog: {
            root: {
                background: '{overlay.modal.background}',
                borderColor: '{overlay.modal.border.color}',
                color: '{overlay.modal.color}',
                borderRadius: '{overlay.modal.border.radius}',
                shadow: '{overlay.modal.shadow}',
            },
            header: {
                padding: '{overlay.modal.header.padding}',
            },
            content: {
                padding: '{overlay.modal.content.padding}',
            },
            footer: {
                padding: '{overlay.modal.footer.padding}',
            },
        },
        tooltip: {
            root: {
                borderRadius: '{border.radius.md}',
                borderColor: '{tlNeutral.150}',
                padding: 'var(--tl-padding-1-5) var(--tl-padding-2-5)',
            },
            colorScheme: {
                light: {
                    root: {
                        background: '{overlay.popover.background}',
                        color: '{overlay.popover.color}',
                    },
                },
            },
        },
        inputgroup: {
            addon: {
                padding: '0 0.5rem',
            },
        },
        popover: {
            shadow: '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)',
            content: {
                padding: 'var(--tl-padding-4)',
            },
            root: {
                borderRadius: 'var(--tl-padding-4)',
                gutter: 'var(--tl-padding-2)',
            },
            css: '.p-popover::before, .p-popover::after { display: none; }',
        },
        tabs: {
            tablist: {
                background: 'transparent',
                borderColor: 'var(--tl-color-border)',
                borderWidth: '0 0 1px 0',
            },
            tab: {
                background: 'transparent',
                hoverBackground: 'transparent',
                activeBackground: 'transparent',
                borderColor: 'transparent',
                hoverBorderColor: 'transparent',
                activeBorderColor: 'transparent',
                color: 'var(--tl-color-muted-foreground)',
                hoverColor: 'var(--tl-color-foreground)',
                activeColor: 'var(--tl-color-foreground)',
                padding: 'var(--tl-padding-2) var(--tl-padding-3) var(--tl-padding-3-5)',
                fontWeight: '600',
                margin: '0',
                gap: 'var(--tl-gap-2)',
            },
            activeBar: {
                height: '2px',
                bottom: '0',
                background: 'var(--tl-color-primary)',
            },
        },
        selectbutton: {
            root: {
                borderRadius: 'var(--tl-radius)',
            },
        },
        togglebutton: {
            root: {
                padding: '0',
                borderRadius: 'calc(var(--tl-radius) - var(--tl-padding-1))',
                fontWeight: '500',
                gap: 'var(--tl-gap-2)',
                sm: {
                    padding: '0',
                },
            },
            content: {
                padding: 'var(--tl-padding-1-5) var(--tl-padding-3)',
                borderRadius: 'calc(var(--tl-radius) - var(--tl-padding-1))',
                checkedShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            },
            colorScheme: {
                light: {
                    root: {
                        background: 'transparent',
                        checkedBackground: 'transparent',
                        hoverBackground: 'transparent',
                        borderColor: 'transparent',
                        checkedBorderColor: 'transparent',
                        color: 'var(--tl-color-muted-foreground)',
                        hoverColor: 'var(--tl-color-foreground)',
                        checkedColor: 'var(--tl-color-foreground)',
                        disabledBackground: 'transparent',
                        disabledBorderColor: 'transparent',
                        disabledColor: 'var(--tl-color-muted-foreground)',
                    },
                    content: {
                        checkedBackground: 'var(--tl-color-background)',
                    },
                },
            },
        },
        breadcrumb: {
            root: {
                background: 'transparent',
                padding: '0',
            },
        },
        button: {
            root: {
                borderRadius: 'var(--tl-radius)',
                gap: 'var(--tl-gap-1)',
                paddingX: 'var(--tl-padding-3)',
                paddingY: 'var(--tl-padding-2)',
                iconOnlyWidth: '2.5rem',
                sm: {
                    paddingX: 'var(--tl-padding-2)',
                    paddingY: 'var(--tl-padding-1-5)',
                    iconOnlyWidth: '2.25rem',
                },
                transitionDuration: '0s',
            },
            colorScheme: {
                light: {
                    root: {
                        primary: {
                            background: 'var(--tl-color-primary)',
                            hoverBackground: 'var(--tl-color-hover-primary)',
                            activeBackground: 'var(--tl-color-hover-primary)',
                            borderColor: 'var(--tl-color-primary)',
                            hoverBorderColor: 'var(--tl-color-hover-primary)',
                            activeBorderColor: 'var(--tl-color-hover-primary)',
                            color: 'var(--tl-color-primary-foreground)',
                            hoverColor: 'var(--tl-color-primary-foreground)',
                            activeColor: 'var(--tl-color-primary-foreground)',
                        },
                        secondary: {
                            background: 'var(--tl-color-secondary)',
                            hoverBackground: 'var(--tl-color-hover-secondary)',
                            activeBackground: 'var(--tl-color-hover-secondary)',
                            borderColor: 'var(--tl-color-secondary)',
                            hoverBorderColor: 'var(--tl-color-hover-secondary)',
                            activeBorderColor: 'var(--tl-color-hover-secondary)',
                            color: 'var(--tl-color-foreground)',
                            hoverColor: 'var(--tl-color-foreground)',
                            activeColor: 'var(--tl-color-foreground)',
                        },
                        warning: {
                            background: 'var(--tl-color-warning)',
                            hoverBackground: 'var(--tl-color-warning)',
                            activeBackground: 'var(--tl-color-warning)',
                            borderColor: 'var(--tl-color-warning)',
                            hoverBorderColor: 'var(--tl-color-warning)',
                            activeBorderColor: 'var(--tl-color-warning)',
                            color: '#fff',
                            hoverColor: '#fff',
                            activeColor: '#fff',
                        },
                        danger: {
                            background: 'var(--tl-color-destructive)',
                            hoverBackground: 'var(--tl-color-hover-destructive)',
                            activeBackground: 'var(--tl-color-hover-destructive)',
                            borderColor: 'var(--tl-color-destructive)',
                            hoverBorderColor: 'var(--tl-color-hover-destructive)',
                            activeBorderColor: 'var(--tl-color-hover-destructive)',
                            color: 'var(--tl-color-destructive-foreground)',
                            hoverColor: 'var(--tl-color-destructive-foreground)',
                            activeColor: 'var(--tl-color-destructive-foreground)',
                        },
                        contrast: {
                            background: 'var(--tl-color-foreground)',
                            hoverBackground: 'var(--tl-color-neutral-800)',
                            activeBackground: 'var(--tl-color-neutral-800)',
                            borderColor: 'var(--tl-color-foreground)',
                            hoverBorderColor: 'var(--tl-color-neutral-800)',
                            activeBorderColor: 'var(--tl-color-neutral-800)',
                            color: 'var(--tl-color-background)',
                            hoverColor: 'var(--tl-color-background)',
                            activeColor: 'var(--tl-color-background)',
                        },
                    },
                    outlined: {
                        primary: {
                            borderColor: 'var(--tl-color-primary)',
                            color: 'var(--tl-color-primary)',
                            hoverBackground: 'var(--tl-color-blue-50)',
                            activeBackground: 'var(--tl-color-blue-100)',
                        },
                        secondary: {
                            borderColor: 'var(--tl-color-border)',
                            hoverBackground: 'var(--tl-color-accent)',
                            activeBackground: 'var(--tl-color-accent)',
                            color: 'var(--tl-color-foreground)',
                        },
                        danger: {
                            hoverBackground: 'var(--tl-color-bg-destructive-10)',
                            activeBackground: 'var(--tl-color-bg-destructive-10)',
                        },
                        contrast: {
                            borderColor: 'var(--tl-color-border)',
                            color: 'var(--tl-color-foreground)',
                            hoverBackground: 'var(--tl-color-accent)',
                            activeBackground: 'var(--tl-color-accent)',
                        },
                    },
                    text: {
                        primary: {
                            color: 'var(--tl-color-primary)',
                            hoverBackground: 'var(--tl-color-blue-50)',
                            activeBackground: 'var(--tl-color-blue-100)',
                        },
                        danger: {
                            color: 'var(--tl-color-destructive)',
                            hoverBackground: 'var(--tl-color-hover-destructive-20)',
                            activeBackground: 'var(--tl-color-hover-destructive-20)',
                        },
                        secondary: {
                            color: 'var(--tl-color-muted-foreground)',
                            hoverBackground: 'transparent',
                            activeBackground: 'transparent',
                        },
                    },
                    link: {
                        color: 'var(--tl-color-primary)',
                        hoverColor: 'var(--tl-color-primary)',
                        activeColor: 'var(--tl-color-primary)',
                    },
                },
            },
        },
        splitbutton: {
            root: {
                borderRadius: 'var(--tl-radius)',
            },
        },
        divider: {
            vertical: {
                padding: 'var(--tl-padding-2) 0',
                margin: '0 var(--tl-padding-2)',
            },
            colorScheme: {
                light: {
                    borderColor: 'var(--tl-color-border)',
                },
            },
        },
        scrollpanel: {
            bar: {
                size: '8px',
            },
            colorScheme: {
                light: {
                    bar: {
                        background: '{tlNeutral.150}',
                    },
                },
            },
        },
        checkbox: {
            root: {
                width: '1rem',
                height: '1rem',
                borderRadius: '{border.radius.sm}',
                borderColor: '{tlNeutral.400}',
                hoverBorderColor: '{tlNeutral.400}',
                focusBorderColor: '{tlNeutral.400}',
                checkedBackground: 'var(--tl-color-primary)',
                checkedHoverBackground: 'var(--tl-color-primary)',
                checkedBorderColor: 'var(--tl-color-primary)',
                checkedHoverBorderColor: 'var(--tl-color-primary)',
                checkedFocusBorderColor: 'var(--tl-color-primary)',
                focusRing: {
                    width: '0',
                    style: 'none',
                    color: 'transparent',
                    offset: '0',
                    shadow: 'var(--tl-focus-ring)',
                },
            },
            icon: {
                size: '0.5rem',
                color: '{tlNeutral.400}',
                checkedColor: 'var(--tl-color-primary-foreground)',
                checkedHoverColor: 'var(--tl-color-primary-foreground)',
            },
        },
        datatable: {
            headerCell: {
                padding: 'var(--tl-padding-3) var(--tl-padding-4)',
                background: 'var(--tl-color-muted)',
                borderColor: 'var(--tl-color-border)',
                selectedBackground: 'var(--tl-color-muted)',
                selectedColor: 'inherit',
            },
            bodyCell: {
                padding: 'var(--tl-padding-2) var(--tl-padding-4)',
                borderColor: 'var(--tl-color-border)',
            },
            row: {
                color: 'var(--tl-color-foreground)',
            },
        },
    },
})
